import { useState } from "react";
import type { FormEvent } from "react";

export type FormStatus = "idle" | "submitting" | "success" | "error";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

interface UseFormSubmitOptions {
  submitAddr: string;
  successMessage?: string;
}

const HONEYPOT_FIELD = "website_alt";

export function useFormSubmit({ submitAddr, successMessage = "已收到，感谢你的反馈。" }: UseFormSubmitOptions) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [error, setError] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot: a filled value means a bot. Fake success, send nothing.
    const honeypot = String(formData.get(HONEYPOT_FIELD) ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === HONEYPOT_FIELD) return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setError("");

    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const lowerMsg = String(serverMsg).toLowerCase();
      const isSpam = lowerMsg.includes("spam") || lowerMsg.includes("form data is spam");
      const codeOk = !parsed?.code || parsed.code === "OK";

      if (!response.ok || !codeOk || isSpam) {
        setStatus("error");
        setError(String(serverMsg) || "提交未成功，请稍后重试。");
        return;
      }

      setStatus("success");
      setError("");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "网络异常，请稍后重试。");
    }
  };

  const reset = () => {
    setStatus("idle");
    setError("");
  };

  return { status, error, successMessage, submit, reset };
}